import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faHeart } from "@fortawesome/free-solid-svg-icons";
import '../css/postComments.css';
import '../css/pagination.css';
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import api from "../api";

const formatRelativeTime = (dateObj, language) => {
    if (!dateObj || !dateObj.date || !dateObj.timezone) {
        return t('admin.unknown');
    }

    const languageMapping = {
        'ua': 'uk',
    };
    const intlLanguage = languageMapping[language] || language;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    moment.locale(intlLanguage);
    const date = moment.utc(dateObj.date).tz(userTimezone);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    let value, unit;
    if (diffInSeconds < 60) {
        value = diffInSeconds;
        unit = 'second';
    } else if (diffInSeconds < 3600) {
        value = Math.floor(diffInSeconds / 60);
        unit = 'minute';
    } else if (diffInSeconds < 86400) {
        value = Math.floor(diffInSeconds / 3600);
        unit = 'hour';
    } else if (diffInSeconds < 2592000) {
        value = Math.floor(diffInSeconds / 86400);
        unit = 'day';
    } else if (diffInSeconds < 31536000) {
        value = Math.floor(diffInSeconds / 2592000);
        unit = 'month';
    } else {
        value = Math.floor(diffInSeconds / 31536000);
        unit = 'year';
    }

    const rtf = new Intl.RelativeTimeFormat(intlLanguage, { numeric: 'auto' });
    return rtf.format(-value, unit);
};

const PostComment = ({ postId, isAuthenticated, userId, isAdmin, isBanned, token, onCommentCountUpdate }) => {
    const { i18n } = useTranslation();
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [dailyCount, setDailyCount] = useState(0);
    const [isDailyLimitReached, setIsDailyLimitReached] = useState(false);
    const [loading, setLoading] = useState(true);
    const [likedComments, setLikedComments] = useState(new Set());

    useEffect(() => {
        loadComments(page);
    }, [page]);

    const loadComments = (page) => {
        setLoading(true);
        api.get(`${process.env.REACT_APP_API_URL}/api/posts/${postId}/comments?page=${page}&limit=5`)
            .then(response => {
                setComments(response.data.comments);
                setTotalPages(Math.ceil(response.data.count / 5));

                if (onCommentCountUpdate) {
                    onCommentCountUpdate(response.data.count);
                }
                if (userId) {
                    api.get(`${process.env.REACT_APP_API_URL}/api/posts/comments/${postId}/user-comments-count`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                        .then(countResponse => {
                            const userCommentCount = countResponse.data.count;
                            setDailyCount(userCommentCount);
                            setIsDailyLimitReached(userCommentCount >= 5);
                        })
                        .catch(error => console.error('Error fetching user comments count', error))
                        .finally(() => setLoading(false));
                } else {
                    setLoading(false);
                }
            })
            .catch(error => {
                console.error('Error fetching comments', error);
                setLoading(false);
            });
    };

    const handleAddComment = () => {
        if (!isAuthenticated) {
            Swal.fire({
                text: t('messages.notLoggedIn'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#d33',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (isBanned) {
            Swal.fire({
                text: t('posts.userBanned'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#d33',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (!commentText.trim()) {
            Swal.fire({
                text: t('posts.emptyComment'),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        api.post(`${process.env.REACT_APP_API_URL}/api/posts/${postId}/comments`, {
            content: commentText
        }, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(response => {
                setCommentText('');
                setComments(prev => [response.data.comment, ...prev]);
                loadComments(page);
                Swal.fire({
                    text: t('posts.commentAdded'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .catch(error => {
                if (error.response && error.response.data && error.response.data.error === 'You have reached the daily limit of 5 comments') {
                    setIsDailyLimitReached(true);
                    setDailyCount(5);
                    Swal.fire({
                        text: t('posts.dailyLimitReached'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                } else {
                    Swal.fire({
                        text: t('posts.errorAddingComment'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                }
                console.error('Error adding comment', error);
            });
    };

    const handleEditComment = (commentId, currentText) => {
        setEditingCommentId(commentId);
        setEditingText(currentText);
    };

    const handleSaveEdit = (commentId) => {
        api.put(`${process.env.REACT_APP_API_URL}/api/posts/comments/${commentId}`, {
            content: editingText
        }, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(response => {
                setComments(comments.map(comment =>
                    comment.id === commentId ? { ...comment, content: editingText } : comment
                ));
                setEditingCommentId(null);
                setEditingText('');
                loadComments(page);
                Swal.fire({
                    text: t('posts.commentUpdated'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .catch(error => {
                Swal.fire({
                    text: t('posts.errorUpdatingComment'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#d33',
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingText('');
    };

    const handleDeleteComment = (commentId) => {
        Swal.fire({
            text: t('posts.confirmDeleteComment'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('posts.delete'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                api.delete(`${process.env.REACT_APP_API_URL}/api/posts/comments/${commentId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                    .then(() => {
                        setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
                        loadComments(page); // Reload to update total count
                        Swal.fire({
                            text: t('posts.commentDeleted'),
                            icon: 'success',
                            background: '#1a1a1a',
                            color: '#b3b3b3',
                            iconColor: '#00ff00',
                            confirmButtonColor: '#3085d6',
                            customClass: {
                                popup: 'swal2-dark-popup',
                                confirmButton: 'swal2-dark-button'
                            }
                        });
                    })
                    .catch(() => {
                        Swal.fire({
                            text: t('posts.error'),
                            icon: 'error',
                            background: '#1a1a1a',
                            color: '#b3b3b3',
                            iconColor: '#ff0000',
                            confirmButtonColor: '#3085d6',
                            confirmButtonText: t('posts.ok'),
                            customClass: {
                                popup: 'swal2-dark-popup',
                                confirmButton: 'swal2-dark-button'
                            }
                        });
                    });
            }
        });
    };

    const handleLikeComment = (commentId) => {
        if (!isAuthenticated) {
            Swal.fire({
                text: t('messages.notLoggedIn'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#d33',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (likedComments.has(commentId)) {
            api.delete(`${process.env.REACT_APP_API_URL}/api/posts/comments/${commentId}/unlike`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(() => {
                    setLikedComments(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(commentId);
                        return newSet;
                    });
                    setComments(prevComments =>
                        prevComments.map(comment =>
                            comment.id === commentId ? { ...comment, likes: comment.likes - 1 } : comment
                        )
                    );
                })
                .catch(() => {
                    Swal.fire({
                        text: t('posts.error'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('posts.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                });
        } else {
            api.post(`${process.env.REACT_APP_API_URL}/api/posts/comments/${commentId}/like`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(() => {
                    setLikedComments(prev => {
                        const newSet = new Set(prev);
                        newSet.add(commentId);
                        return newSet;
                    });
                    setComments(prevComments =>
                        prevComments.map(comment =>
                            comment.id === commentId ? { ...comment, likes: comment.likes + 1 } : comment
                        )
                    );
                })
                .catch(() => {
                    Swal.fire({
                        text: t('posts.alreadyLiked'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('posts.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                });
        }
    };

    const handlePageChange = (event) => {
        setPage(event.selected + 1);
    };

    return (
        <div className="post-comments-section">
            {/*{<h3>{t('posts.comments')} {isAuthenticated && !loading && `Limit: ${dailyCount}/5 per day (${5 - dailyCount} left)`}</h3>}*/}
            {isAuthenticated && !isBanned && !loading && !isDailyLimitReached && (
                <div className="comment-form">
                    <textarea
                        className="post-comment-textarea"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder={t('posts.addComment')}
                    />
                    <button className="create-comment" onClick={handleAddComment}>{t('posts.send')}</button>
                </div>
            )}
            {isAuthenticated && !loading && isDailyLimitReached && (
                <p>{t('posts.dailyLimitReached')}</p>
            )}
            {loading && <p>{t('posts.loadingComments')}</p>}
            {!loading && comments.map(comment => (
                <article key={comment.id} className="post-comment-item">
                    {editingCommentId === comment.id ? (
                        <form className="comment-edit-form">
                            <textarea
                                className="post-comment-textarea"
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                            />
                            <button className="save-comment" type="button" onClick={() => handleSaveEdit(comment.id)}>
                                {t('posts.save')}
                            </button>
                            <button className="cancel-comment" type="button" onClick={handleCancelEdit}>
                                {t('posts.cancel')}
                            </button>
                        </form>
                    ) : (
                        <div className="post-comment-content">
                            <header className="comment-header">
                                <span>
                                    <Link to={`/user/${comment.user_id}`} className="post-username">
                                        {comment.username}
                                    </Link>{' '}
                                    -{' '}
                                    <time dateTime={comment.created_at}>
                                        {formatRelativeTime(comment.created_at, i18n.language)}
                                    </time>
                                </span>
                            </header>
                            <p className="comment-text">{comment.content}</p>
                            <footer className="comment-actions-wrapper">
                                <span
                                    className={`like-comment-button ${likedComments.has(comment.id) ? 'liked' : ''}`}
                                    onClick={() => handleLikeComment(comment.id)}
                                    style={{ cursor: isAuthenticated ? 'pointer' : 'default' }}
                                    title={likedComments.has(comment.id) ? t('posts.unlike') : t('posts.like')}
                                >
                                    <FontAwesomeIcon icon={faHeart} className="icon" />
                                    <span
                                        className="like-count"
                                        style={{ cursor: isAuthenticated ? 'pointer' : 'default' }}
                                    >
                                        {comment.likes}
                                    </span>
                                </span>
                                {isAuthenticated && (comment.user_id === userId || isAdmin) && (
                                    <div className="comment-actions">
                                        <FontAwesomeIcon
                                            icon={faEdit}
                                            className="edit-comment-icon"
                                            onClick={() => handleEditComment(comment.id, comment.content)}
                                            title={t('posts.editComment')}
                                        />
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            className="delete-comment-icon"
                                            onClick={() => handleDeleteComment(comment.id)}
                                            title={t('posts.deleteComment')}
                                        />
                                    </div>
                                )}
                            </footer>
                        </div>
                    )}
                </article>
            ))}
            {!loading && totalPages > 1 && (
                <ReactPaginate
                    breakLabel="..."
                    nextLabel={null}
                    onPageChange={handlePageChange}
                    pageRangeDisplayed={1}
                    marginPagesDisplayed={2}
                    pageCount={totalPages}
                    previousLabel={null}
                    forcePage={page - 1}
                    containerClassName="pagination-container"
                    pageClassName="pagination-item"
                    pageLinkClassName="pagination-link"
                    previousClassName="pagination-item"
                    nextClassName="pagination-next"
                    previousLinkClassName="pagination-previous"
                    nextLinkClassName="pagination-link"
                    breakClassName="pagination-item"
                    breakLinkClassName="pagination-link"
                    activeClassName="active"
                />
            )}
        </div>
    );
};

export default PostComment;